import { Person } from '@mui/icons-material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import {
  ButtonCellComponent,
  EmployeeCellComponent,
  SolaForceDataGrid,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { useAllOrgUnits } from '~/data/orgunits';
import {
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  PersonnelContactsReportData,
  usePersonnelContactsReportData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportViewLayout,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import { getEnumName } from '~/utils/helpers/enum';

export const PersonnelContactsReport = () => {
  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const apiRef = useGridApiRef();

  const { orgUnits } = useAllOrgUnits();
  const { getEmployeeById } = useAllEmployees();
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const { getEnums } = useAllEnums();

  const enums = useMemo(() => {
    return {
      countryEnums: getEnums(EnumListKey.COUNTRY),
    };
  }, [getEnums]);

  const { data, isLoading } = usePersonnelContactsReportData({
    reportType: {
      type: AnalyticsReportTypes.HR,
      employeeId: loggedInEmployeeId,
      role: AnalyticsReportRoleScope.Manager,
      scope: AnalyticsReportScope.Direct,
    },
  });

  const dataColumnOptions = useMemo(() => {
    return data.reduce(
      (acc, row) => {
        const { fEmpHomeCountry } = row;

        const includeIfMissing = (options: string[], value: string) => {
          if (value && !options.includes(value)) {
            options.push(value);
          }
        };

        includeIfMissing(acc.countryOptions, fEmpHomeCountry);

        return acc;
      },
      {
        countryOptions: [],
      } as {
        countryOptions: string[];
      },
    );
  }, [data]);

  const getEnumValueOptions = (
    enumOptions: Enum[],
    filterOptionCodes?: string[],
  ) => {
    const availableEnums = filterOptionCodes
      ? enumOptions.filter(({ code }) => filterOptionCodes.includes(code))
      : enumOptions;

    return availableEnums.map((e) => e.name);
  };

  const columns = useMemo((): GridColDef<PersonnelContactsReportData>[] => {
    const { countryEnums } = enums;
    const { countryOptions } = dataColumnOptions;

    return [
      {
        field: 'fEmpId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'fEmpFirstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'fEmpLastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'empNum',
        headerName: t('laEmpId'),
        valueGetter: ({ row: { fEmpId } }) =>
          getEmployeeById(fEmpId)?.fEmpPersonNumber,
        minWidth: 130,
      },
      {
        field: 'fEmpUnit',
        headerName: t('laOrgUnit'),
        minWidth: 180,
        valueGetter: ({ row }) => {
          const orgUnit = orgUnits?.find(
            (ou) => ou.fOrgUnitId === row.fEmpUnitId,
          );
          return orgUnit ? orgUnit.fOrgUnitName : '';
        },
      },
      {
        field: 'fEmpUnitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 110,
        valueGetter: ({ row }) => {
          const orgUnit = orgUnits?.find(
            (ou) => ou.fOrgUnitId === row.fEmpUnitId,
          );
          return orgUnit ? orgUnit.fOrgUnitNumber : '';
        },
      },
      {
        field: 'fEmpPhoneWork',
        headerName: t('laWorkPhone'),
        minWidth: 180,
      },
      {
        field: 'fEmpPhoneMob',
        headerName: t('laWorkMobile'),
        minWidth: 180,
      },
      {
        field: 'fEmpEmailWork',
        headerName: t('laWorkEmail'),
        minWidth: 280,
      },
      {
        field: 'fEmpWorkOfficeLocation',
        headerName: t('laWorkAddress'),
        minWidth: 150,
      },
      {
        field: 'fEmpPhoneHome',
        headerName: t('laHomePhone'),
        minWidth: 180,
      },
      {
        field: 'fEmpEmailHome',
        headerName: t('laHomeEmail'),
        minWidth: 230,
      },
      {
        field: 'fEmpHomeStreetAddress1',
        headerName: t('laHomeAddress'),
        minWidth: 150,
      },
      {
        field: 'fEmpHomePostalCode',
        headerName: t('laHomePostalCode'),
        minWidth: 150,
      },
      {
        field: 'fEmpHomeCity',
        headerName: t('laHomeCity'),
        minWidth: 110,
      },
      {
        field: 'fEmpHomeState',
        headerName: t('laHomeState'),
        minWidth: 170,
      },
      {
        field: 'fEmpHomeCountry',
        headerName: t('laHomeCountry'),
        type: 'singleSelect',
        valueGetter: ({ value }) => getEnumName(countryEnums, value),
        valueOptions: getEnumValueOptions(countryEnums, countryOptions),
        minWidth: 150,
      },
    ];
  }, [
    t,
    getEmployeeById,
    goToEmployeeBasicDataView,
    dataColumnOptions,
    enums,
    orgUnits,
  ]);

  return (
    <ReportViewLayout
      heading={t('laContactReport')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout>
              <ReportDatasetRowCount
                count={data.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility apiRef={apiRef} isLoading={isLoading} />
              <ReportDataExport
                title={t('laContactReport')}
                apiRef={apiRef}
                isLoading={isLoading}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={data}
        loading={isLoading}
        density={'compact'}
        getRowId={(row) => row.fEmpId}
        pinnedColumns={{
          left: ['fEmpId', 'fEmpFirstName', 'fEmpLastName'],
        }}
      />
    </ReportViewLayout>
  );
};
